import { useEffect } from 'react';
import { TextMessagingConfiguration } from '@mews-commander/data-access';
import { LocalizedCurrencyValue, LocalizedText, useFeatureFlag, useGainsightService } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { Card, Stack, Typography } from '@optimus-web/core';
import { GeneralFormContainer } from '../../GeneralForm/GeneralFormContainer';
import { Row } from './Row';
import { ConfigurationBanner } from './ConfigurationBanner';
import { AvailableBudget } from './AvailableBudget/AvailableBudget';

interface GeneralConfigurationProps {
    configuration: TextMessagingConfiguration;
    enterpriseId: string;
}

export const GeneralConfiguration = ({ configuration, enterpriseId }: GeneralConfigurationProps) => {
    const gainsightService = useGainsightService();

    const { availableBudget, amountSpentThisMonth, totalMessagesSentThisMonth } = configuration;
    const { totalBudget, remainingBudget } = availableBudget;

    useEffect(() => {
        const {
            fee,
            maxMonthlyBudget,
            rolloverFromPriorMonths,
            startingBudget,
            startingBudgetForNextMonth,
            toppedUpFee,
        } = availableBudget;

        gainsightService.trackEvent('TextMessagingConfigurationGeneralTabAvailableBudgetDisplayed', {
            fee,
            maxMonthlyBudget,
            remainingBudget,
            rolloverFromPriorMonths,
            startingBudget,
            startingBudgetForNextMonth,
            toppedUpFee,
            totalBudget,
        });
    }, [availableBudget, gainsightService, remainingBudget, totalBudget]);

    const areSeparateSMSTypesEnabled = useFeatureFlag(FeatureFlag.separateSMSTypesEnabled);

    const rows = (
        <>
            <Row
                content={<span data-test-id="total-messages-sent-this-month">{totalMessagesSentThisMonth}</span>}
                label={TextKey.SentThisMonth}
                messageTextKey={TextKey.TextMessagingConfigurationSentThisMonthTooltip}
            />
            <Row
                content={
                    <LocalizedCurrencyValue
                        data-test-id="amount-spent-this-month"
                        value={{
                            value: amountSpentThisMonth.value,
                            currency: amountSpentThisMonth.currencyCode,
                        }}
                    />
                }
                label={TextKey.SpentThisMonth}
                messageTextKey={TextKey.TextMessagingConfigurationSpentThisMonthTooltip}
                hasDivider={!areSeparateSMSTypesEnabled}
            />
        </>
    );

    if (areSeparateSMSTypesEnabled) {
        return (
            <Stack vertical spacing="500">
                <Card padding="200">
                    <Stack vertical spacing="200">
                        <ConfigurationBanner configuration={configuration} />
                        <AvailableBudget remainingBudget={remainingBudget} totalBudget={totalBudget} />
                        {rows}
                    </Stack>
                </Card>
                <GeneralFormContainer hasSenderNumber enterpriseId={enterpriseId} configuration={configuration} />
            </Stack>
        );
    }

    return (
        <>
            <Stack vertical spacing="100">
                <Typography textStyle="titleSmall" as="p">
                    <LocalizedText textKey={TextKey.TextMessagingConfigurationGeneralDescription} />
                </Typography>
                <ConfigurationBanner configuration={configuration} />
            </Stack>
            <Stack vertical spacing="200">
                <AvailableBudget remainingBudget={remainingBudget} totalBudget={totalBudget} />
                {rows}
                <GeneralFormContainer
                    hasSenderNumber={false}
                    enterpriseId={enterpriseId}
                    configuration={configuration}
                />
            </Stack>
        </>
    );
};
